import { useNavigate, Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { ChevronRightIcon, CalendarDaysIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { useApi } from '../hooks/useApi'
import { api } from '../services/api'
import EventForm from '../components/events/EventForm'
import { trackEngagement, trackConversion } from '../utils/analytics'
import { eventPath } from '../utils/slugs'

export default function NewEventPage() {
  usePageTitle('Create a Garage Sale Event')
  const navigate = useNavigate()
  const { execute: submitEvent, loading, error } = useApi()

  const handleSubmit = async (data) => {
    try {
      const response = await api.events.create(data)
      trackEngagement.eventCreated(data.event_type || 'garage_sale')
      trackConversion.eventPublished(response.id, data.event_type || 'garage_sale')
      navigate(eventPath(response))
    } catch (error) {
      // Error handled by useApi
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-text-muted mb-6">
        <Link to="/events" className="link-subtle hover:text-text">Events</Link>
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="text-text font-medium">New Event</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start gap-3.5 mb-8">
        <div className="rounded-xl bg-primary-50 dark:bg-primary-50 p-2.5 flex-shrink-0
          ring-4 ring-primary-50/50 dark:ring-primary-50/30">
          <CalendarDaysIcon className="h-6 w-6 text-primary-600 dark:text-primary-500" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text leading-tight">Create an Event</h1>
          <p className="text-text-muted mt-1 text-sm sm:text-base">
            Organize a garage sale event for your neighborhood
          </p>
        </div>
      </div>

      {/* Welcome hint */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.25 }}
        className="mb-6 flex items-start gap-2.5 rounded-xl
          bg-primary-50 dark:bg-primary-50 border border-primary-200 dark:border-primary-200
          p-3.5 text-sm text-primary-700 dark:text-primary-600"
      >
        <SparklesIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          Events bring together multiple garage sales in one area.
          Once created, other sellers can add their sales to your event.
        </p>
      </motion.div>

      <EventForm
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        submitLabel="Submit Event"
      />
    </motion.div>
  )
}
